import type { Vec3 } from "./coords";

/**
 * 模型模式（教学挂图）的显示变换，见 docs/adr/0003。
 * 场景单位：海王星轨道（30 AU）半径 ≈ MODEL_NEPTUNE_RADIUS。
 * 方向保持真实（日心黄经/黄纬不变），只压缩径向距离，所以“今天火星在哪一侧”依然正确。
 */
export const MODEL_NEPTUNE_RADIUS = 100;
const R0 = 0.25; // AU，对数压缩的“膝点”
const NORM = Math.log(1 + 30 / R0);

/** 真实日心距离（AU）→ 显示半径（场景单位）。单调递增，r=0 → 0。 */
export function modelRadial(rAU: number): number {
  return (MODEL_NEPTUNE_RADIUS * Math.log(1 + rAU / R0)) / NORM;
}

/** 日心向量（AU）→ 显示向量（保持方向）。 */
export function modelPosition(v: Vec3): Vec3 {
  const r = Math.hypot(v.x, v.y, v.z);
  if (r === 0) return { x: 0, y: 0, z: 0 };
  const k = modelRadial(r) / r;
  return { x: v.x * k, y: v.y * k, z: v.z * k };
}

/** 真实平均半径（km）→ 显示半径（场景单位）：幂律压缩，保持大小顺序。 */
export function modelBodyRadius(radiusKm: number): number {
  return 0.094 * Math.pow(radiusKm, 0.4);
}

/** 模型模式下太阳的显示半径（上限，避免吞掉水星轨道）。 */
export const MODEL_SUN_RADIUS = 9;
/** 月球绕地轨道的显示半径（固定放大，界面需标注“已放大”）。 */
export const MODEL_MOON_ORBIT_RADIUS = 7.5;
/** 月球显示半径（固定值）。 */
export const MODEL_MOON_RADIUS = 0.9;

// ── 真实比例模式（S3）──────────────────────────────────────────────
import { AU_KM } from "./coords";

/** 真实比例下 1 AU 对应的场景单位。与模型模式共用同一单位（海王星轨道 ≈ 100），便于两种模式之间插值。 */
export const REAL_UNITS_PER_AU = MODEL_NEPTUNE_RADIUS / 30;

/** 真实半径（km）→ 场景单位（真实比例）。 */
export const realBodyRadius = (radiusKm: number): number => (radiusKm / AU_KM) * REAL_UNITS_PER_AU;

/** 在两个正数之间按对数插值（用于半径、放大系数：跨越数个数量级时线性插值会让中间态几乎等于较大者）。 */
export function logLerp(a: number, b: number, t: number): number {
  return Math.exp(Math.log(a) * (1 - t) + Math.log(b) * t);
}
